import {
  LS_KEY_SIGNUP_SOURCE_URL,
  LS_KEY_SIGNUP_REFERRER_URL,
  LS_KEY_SIGNUP_REFERRAL_HASH,
  SS_KEY_LOGIN_SOURCE_URL,
  SS_KEY_LOGIN_REFERRER_URL,
} from 'src/constants/localStorageKeys';

// First-touch attribution: capture the landing URL and HTTP referrer on the
// very first visit, regardless of whether UTM params are present. Never
// overwrite — the original entry point is what matters for attribution.
// Sent as `source_url` / `referrer_url` on signup so attribution survives
// users who land today and convert days later.
export const captureUtmsFromUrl = (): void => {
  try {
    if (localStorage.getItem(LS_KEY_SIGNUP_SOURCE_URL)) return;
    localStorage.setItem(LS_KEY_SIGNUP_SOURCE_URL, window.location.href);
    if (document.referrer) {
      localStorage.setItem(LS_KEY_SIGNUP_REFERRER_URL, document.referrer);
    }
  } catch {
    // localStorage unavailable (private mode, quota) — attribution is best-effort
  }
};

// The landing (Next.js) site at abekus.ai stashes the referral hash under this
// key. It shares this SPA's origin in prod, so read it as a fallback: a referral
// captured on the marketing site (the usual entry point) survives the handoff
// into signup even after the `?referral_hash=` URL param drops off during nav.
const LS_KEY_LANDING_REFERRAL_HASH = 'referral_hash';

// First-touch referral capture: a link like `?referral_hash=KUSM9688` stamps the
// landing URL before the user reaches the signup form (often on a different page).
// Stash it on first visit so it survives navigation and is sent as `referred_by_hash`
// on signup. Never overwrite — the first referrer who brought the user in gets credit.
export const captureReferralHashFromUrl = (): void => {
  try {
    if (localStorage.getItem(LS_KEY_SIGNUP_REFERRAL_HASH)) return;
    const referralHash =
      new URLSearchParams(window.location.search).get('referral_hash') ||
      localStorage.getItem(LS_KEY_LANDING_REFERRAL_HASH);
    if (referralHash) {
      localStorage.setItem(LS_KEY_SIGNUP_REFERRAL_HASH, referralHash);
    }
  } catch {
    // localStorage unavailable (private mode, quota) — referral is best-effort
  }
};

export const getStoredReferralHash = (): string => {
  try {
    return (
      localStorage.getItem(LS_KEY_SIGNUP_REFERRAL_HASH) ||
      localStorage.getItem(LS_KEY_LANDING_REFERRAL_HASH) ||
      ''
    );
  } catch {
    return '';
  }
};

export const getStoredSourceUrl = (): string => {
  try {
    return (
      localStorage.getItem(LS_KEY_SIGNUP_SOURCE_URL) || window.location.href
    );
  } catch {
    return window.location.href;
  }
};

export const getStoredReferrerUrl = (): string => {
  try {
    return localStorage.getItem(LS_KEY_SIGNUP_REFERRER_URL) || '';
  } catch {
    return '';
  }
};

// Per-session attribution, the login-side counterpart of `captureUtmsFromUrl`.
// Stored in sessionStorage so a new tab (the usual shape of "clicked the link
// in the digest email") starts a fresh capture, while navigating inside the
// SPA does not overwrite the entry point. Sent as `source_url` /
// `referrer_url` on login so the backend can record what drove this visit
// rather than re-reporting the signup channel forever.
export const captureSessionSourceFromUrl = (): void => {
  try {
    if (sessionStorage.getItem(SS_KEY_LOGIN_SOURCE_URL)) return;
    sessionStorage.setItem(SS_KEY_LOGIN_SOURCE_URL, window.location.href);
    if (document.referrer) {
      sessionStorage.setItem(SS_KEY_LOGIN_REFERRER_URL, document.referrer);
    }
  } catch {
    // sessionStorage unavailable (private mode, quota) — attribution is best-effort
  }
};

export const getSessionSourceUrl = (): string => {
  try {
    return (
      sessionStorage.getItem(SS_KEY_LOGIN_SOURCE_URL) || window.location.href
    );
  } catch {
    return window.location.href;
  }
};

export const getSessionReferrerUrl = (): string => {
  try {
    return sessionStorage.getItem(SS_KEY_LOGIN_REFERRER_URL) || '';
  } catch {
    return '';
  }
};
